/**
 * Captura del score de un carril.
 *
 * Pensada para una tablet, de pie, con guantes de levantamiento y el
 * atleta esperando: botones grandes, un campo obligatorio a la vez y
 * ningun estado ambiguo. Guardar nunca falla -- va al outbox.
 */

import { useEffect, useState } from 'react';
import {
  capRemaining,
  formatDuration,
  formatDurationPrecise,
  parseTimeInput,
  repsFromProgress,
  type ScoreStatus,
  type WodSegment,
} from '@timecap/core';

import { SegmentPicker } from './SegmentPicker.js';

import type { LaneRow, ScoreRow, WorkoutSummary } from '../lib/api.js';
import type { ScorePayload } from '../lib/queue.js';

interface Props {
  workout: WorkoutSummary;
  lane: LaneRow;
  existing: ScoreRow | undefined;
  /**
   * Segundos capturados al tocar "Terminó", si se llegó por ahí.
   *
   * Manda sobre `existing`: el juez acaba de marcar el corte y eso es
   * más reciente que cualquier cosa guardada antes.
   */
  capturedSeconds?: number | null;
  /** Los movimientos del WOD. Vacio = el juez teclea las reps a mano. */
  segments?: WodSegment[];
  /** Arranque del heat, para seguir contando dentro de la ficha. */
  heatStartedAt?: string | null;
  /** Reloj del servidor menos el de esta tablet. */
  offsetMs?: number;
  eventId: string;
  judgeId: string | null;
  onSave: (payload: ScorePayload) => void;
  onClose: () => void;
}

/**
 * Como juecea ESTE juez, recordado en su tablet.
 *
 * Son dos formas de trabajar, no dos pantallas:
 *
 *   tiempo directo  -- toca el carril cuando el atleta cruza y ya
 *   marcando        -- va tocando los movimientos conforme los hace
 *
 * Un juez trabaja igual toda la jornada, asi que la eleccion se guarda y
 * no hay que repetirla en cada ficha. Va en localStorage y no en la base
 * porque es una preferencia de quien tiene la tablet en la mano, no un
 * dato de la competencia.
 */
const MODO_KEY = 'timecap.judge.marcando';

function leerModo(): boolean {
  try {
    return localStorage.getItem(MODO_KEY) === '1';
  } catch {
    // Navegador con el almacenamiento bloqueado: se empieza en tiempo
    // directo, que es el modo que no necesita nada.
    return false;
  }
}

function guardarModo(v: boolean) {
  try {
    localStorage.setItem(MODO_KEY, v ? '1' : '0');
  } catch {
    /* sin guardar; el modo vale para esta ficha */
  }
}

export function ScoreSheet({
  workout,
  lane,
  existing,
  capturedSeconds,
  segments = [],
  heatStartedAt,
  offsetMs = 0,
  eventId,
  judgeId,
  onSave,
  onClose,
}: Props) {
  const [status, setStatus] = useState<ScoreStatus>(existing?.status ?? 'submitted');
  const [capped, setCapped] = useState(existing?.capped ?? false);
  const [timeText, setTimeText] = useState(
    capturedSeconds != null
      ? (formatDurationPrecise(capturedSeconds) ?? '')
      : existing?.time_seconds != null
        ? (formatDuration(existing.time_seconds) ?? '')
        : '',
  );
  const [reps, setReps] = useState(existing?.reps != null ? String(existing.reps) : '');
  const [loadKg, setLoadKg] = useState(existing?.load_kg != null ? String(existing.load_kg) : '');
  const [distanceM, setDistanceM] = useState(
    existing?.distance_m != null ? String(existing.distance_m) : '',
  );
  const [tiebreakText, setTiebreakText] = useState(
    existing?.tiebreak_seconds != null ? (formatDuration(existing.tiebreak_seconds) ?? '') : '',
  );
  const [notes, setNotes] = useState(existing?.notes ?? '');
  // El avance guardado, para poder reabrir la ficha y corregir "eran
  // seis, no cinco" sin volver a recorrer la lista.
  // Si el score guardado ya trae avance, este juez estaba marcando.
  const [marcando, setMarcando] = useState(
    () => existing?.segments_done != null || leerModo(),
  );
  const [segmentsDone, setSegmentsDone] = useState(existing?.segments_done ?? 0);
  const [partialReps, setPartialReps] = useState(existing?.partial_reps ?? 0);
  const [error, setError] = useState<string | null>(null);

  const scored = status === 'submitted' || status === 'validated';
  const isTime = workout.score_type === 'time';
  /**
   * La lista esta siempre que el WOD tenga movimientos definidos.
   *
   * Dos usos, el mismo gesto:
   *
   *   - DURANTE el heat: el juez va marcando por donde va el atleta. No
   *     es su marca --si termina, su marca es el tiempo-- es el avance.
   *   - AL LLEGAR AL CAP: ese avance SE CONVIERTE en su marca, y de ahi
   *     sale el CAP+N.
   *
   * Antes solo aparecia con "llego al time cap" marcado, asi que el modo
   * en vivo no existia: habia que esperar al corte para poder tocar nada.
   *
   * En un AMRAP no sale: el atleta da vueltas al WOD y esta lista no sabe
   * contar rondas. Ahi se sigue tecleando el numero.
   */
  // Al llegar al cap la lista sale igual: ahi hacen falta las reps, y
  // tenerlas marcadas es mejor que teclear un numero de memoria.
  const hayLista = segments.length > 0 && isTime;
  const conLista = hayLista && (marcando || capped);
  /** El avance es la marca solo cuando no termino dentro del cap. */
  const avanceEsLaMarca = conLista && capped;

  /**
   * El heat sigue corriendo mientras el juez rellena esta ficha.
   *
   * Cada 50 ms porque aqui se ven centesimas; el contador grande de
   * fuera va a 200 ms, que sobra para leer segundos.
   */
  const [ahora, setAhora] = useState(() => Date.now());
  useEffect(() => {
    if (!heatStartedAt) return;
    const id = setInterval(() => setAhora(Date.now()), 50);
    return () => clearInterval(id);
  }, [heatStartedAt]);

  const enCurso = heatStartedAt
    ? Math.max(0, (ahora + offsetMs - new Date(heatStartedAt).getTime()) / 1000)
    : null;

  function build(): ScorePayload | null {
    let timeSeconds: number | null = null;
    let repsValue: number | null = null;
    let loadValue: number | null = null;
    let distanceValue: number | null = null;

    if (scored) {
      if (isTime && !capped) {
        timeSeconds = parseTimeInput(timeText);
        if (timeSeconds === null) {
          setError('Escribe el tiempo como mm:ss (por ejemplo 8:12).');
          return null;
        }
      }
      if (isTime && capped) {
        if (avanceEsLaMarca) {
          // Las reps se mandan calculadas para el pintado optimista, pero
          // la version que manda es la que deriva Postgres del avance.
          repsValue = repsFromProgress(segments, { segmentsDone, partialReps });
        } else {
          repsValue = reps.trim() === '' ? null : Number(reps);
          if (repsValue === null || Number.isNaN(repsValue)) {
            setError('Con time cap hay que anotar cuantas reps completo.');
            return null;
          }
        }
      }
      if (workout.score_type === 'reps') {
        repsValue = reps.trim() === '' ? null : Number(reps);
        if (repsValue === null || Number.isNaN(repsValue)) {
          setError('Anota las repeticiones.');
          return null;
        }
      }
      if (workout.score_type === 'load') {
        loadValue = loadKg.trim() === '' ? null : Number(loadKg);
        if (loadValue === null || Number.isNaN(loadValue)) {
          setError('Anota el peso levantado.');
          return null;
        }
      }
      if (workout.score_type === 'distance') {
        distanceValue = distanceM.trim() === '' ? null : Number(distanceM);
        if (distanceValue === null || Number.isNaN(distanceValue)) {
          setError('Anota la distancia.');
          return null;
        }
      }
    }

    let tiebreakSeconds: number | null = null;
    if (scored && workout.tiebreak_enabled && tiebreakText.trim() !== '') {
      tiebreakSeconds = parseTimeInput(tiebreakText);
      if (tiebreakSeconds === null) {
        setError('El tiebreak tambien va como mm:ss.');
        return null;
      }
    }

    return {
      event_id: eventId,
      workout_id: workout.id,
      registration_id: lane.registration_id,
      status,
      time_seconds: timeSeconds,
      reps: repsValue,
      load_kg: loadValue,
      distance_m: distanceValue,
      capped: scored && isTime ? capped : false,
      tiebreak_seconds: tiebreakSeconds,
      notes: notes.trim() === '' ? null : notes.trim(),
      judge_id: judgeId,
      segments_done: conLista ? segmentsDone : null,
      partial_reps: conLista ? partialReps : null,
    };
  }

  function handleSave() {
    setError(null);
    const payload = build();
    if (payload) onSave(payload);
  }

  const repsRemaining =
    avanceEsLaMarca
      ? capRemaining(segments, repsFromProgress(segments, { segmentsDone, partialReps }))
      : capped && workout.rep_target != null && reps.trim() !== ''
        ? Math.max(workout.rep_target - Number(reps), 0)
        : null;

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true">
      <div className="sheet">
        <header className="sheet-head">
          <div>
            <span className="lane-chip">Carril {lane.lane_number}</span>
            <h2>{lane.display_name}</h2>
            <p className="muted">
              {lane.bib_number != null && <>#{lane.bib_number} · </>}
              {workout.name}
            </p>
          </div>
          <button className="ghost" onClick={onClose} aria-label="Cerrar">
            ✕
          </button>
        </header>

        {/* El reloj del heat, dentro de la ficha.
            El tiempo del atleta NO se toma aquí: se tomó en el instante
            en que el juez tocó el carril, y por eso puede rellenar esto
            con calma sin que la marca cambie. Se enseña el reloj para
            que esa relación se vea, en vez de tener que creérsela. */}
        {capturedSeconds != null ? (
          <div className="sheet-clock">
            <span className="sheet-clock-cap">{formatDurationPrecise(capturedSeconds)}</span>
            <small>marca tomada al tocar el carril</small>
          </div>
        ) : enCurso !== null ? (
          <div className="sheet-clock">
            <span className="sheet-clock-run">{formatDurationPrecise(enCurso)}</span>
            <small>heat en curso</small>
          </div>
        ) : (
          // Heat sin arrancar: antes la ficha salia sin nada arriba y
          // parecia que el cronometro no existia en esta pantalla. El
          // tope sirve de referencia mientras se espera la salida.
          workout.time_cap_seconds != null && (
            <div className="sheet-clock">
              <span className="sheet-clock-run">
                {formatDuration(workout.time_cap_seconds)}
              </span>
              <small>time cap · el heat no ha arrancado</small>
            </div>
          )
        )}

        <div className="sheet-body">
          <div className="status-row">
            {(
              [
                ['submitted', 'Marcó'],
                ['dnf', 'DNF'],
                ['dq', 'DQ'],
                ['dns', 'No salió'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                className={`status-btn ${status === value ? 'on' : ''}`}
                onClick={() => setStatus(value)}
              >
                {label}
              </button>
            ))}
          </div>

          {scored && (
            <>
              {isTime && (
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={capped}
                    onChange={(e) => setCapped(e.target.checked)}
                  />
                  <span>Llegó al time cap</span>
                </label>
              )}

              {isTime && !capped && (
                <Field label="Tiempo">
                  <input
                    className="big"
                    inputMode="numeric"
                    placeholder="8:12"
                    value={timeText}
                    onChange={(e) => setTimeText(e.target.value)}
                    autoFocus
                  />
                </Field>
              )}

              {/* Los dos modos de juecear. Solo se ofrece mientras el
                  atleta sigue en carrera: pasado el cap la lista hace
                  falta de todas formas. */}
              {hayLista && !capped && (
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={marcando}
                    onChange={(e) => {
                      setMarcando(e.target.checked);
                      guardarModo(e.target.checked);
                    }}
                  />
                  <span>Ir marcando los movimientos</span>
                </label>
              )}

              {conLista && (
                <SegmentPicker
                  segments={segments}
                  segmentsDone={segmentsDone}
                  partialReps={partialReps}
                  esLaMarca={avanceEsLaMarca}
                  onChange={(d, p) => {
                    setSegmentsDone(d);
                    setPartialReps(p);
                  }}
                />
              )}

              {!avanceEsLaMarca && ((isTime && capped) || workout.score_type === 'reps') && (
                <Field
                  label={isTime ? 'Reps completadas' : 'Repeticiones'}
                  hint={
                    repsRemaining !== null
                      ? `Se registrará como CAP+${repsRemaining}`
                      : workout.rep_target
                        ? `Total del WOD: ${workout.rep_target}`
                        : undefined
                  }
                >
                  <input
                    className="big"
                    inputMode="numeric"
                    placeholder="0"
                    value={reps}
                    onChange={(e) => setReps(e.target.value)}
                    autoFocus
                  />
                </Field>
              )}

              {workout.score_type === 'load' && (
                <Field label="Peso (kg)">
                  <input
                    className="big"
                    inputMode="decimal"
                    placeholder="102.5"
                    value={loadKg}
                    onChange={(e) => setLoadKg(e.target.value)}
                    autoFocus
                  />
                </Field>
              )}

              {workout.score_type === 'distance' && (
                <Field label="Distancia (m)">
                  <input
                    className="big"
                    inputMode="decimal"
                    placeholder="1250"
                    value={distanceM}
                    onChange={(e) => setDistanceM(e.target.value)}
                    autoFocus
                  />
                </Field>
              )}

              {workout.tiebreak_enabled && (
                <Field label="Tiebreak" hint={workout.tiebreak_label ?? undefined}>
                  <input
                    inputMode="numeric"
                    placeholder="3:10"
                    value={tiebreakText}
                    onChange={(e) => setTiebreakText(e.target.value)}
                  />
                </Field>
              )}
            </>
          )}

          <Field label="Nota (opcional)">
            <input
              placeholder="No-rep en la última serie…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Field>

          {error && <p className="error">{error}</p>}
        </div>

        <footer className="sheet-foot">
          <button className="ghost" onClick={onClose}>
            Cancelar
          </button>
          <button className="primary" onClick={handleSave}>
            Guardar
          </button>
        </footer>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </label>
  );
}
